import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Mail, Users2, Coins } from "lucide-react";
import { api, fmtDate } from "../../lib/api";
import { localeOf } from "../../i18n";
import { StatusBadge } from "../../components/StatusBadge";

const Stat = ({ label, value, testId }) => (
  <div className="vy-card p-4" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 font-display text-2xl font-extrabold tabular-nums">{value ?? 0}</p>
  </div>
);

export default function ParticipantDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/analytics/ambassadors/${id}`).then(({ data }) => setData(data)); }, [id]);

  if (!data) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;
  const { user, totals, timeline, ledger } = data;

  return (
    <div className="space-y-6" data-testid="participant-detail-page">
      <Link to="/staff/analytics" data-testid="participant-back-link"
        className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />{t("common.back")}
      </Link>

      <header className="vy-card p-6">
        <h1 className="font-display text-3xl font-extrabold" data-testid="participant-name">{user.name}</h1>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><Mail className="h-4 w-4" />{user.email}</span>
          <span className="inline-flex items-center gap-1.5"><Users2 className="h-4 w-4" />{user.team_name || "—"}</span>
          <span className="inline-flex items-center gap-1.5"><Coins className="h-4 w-4" />{totals.points} {t("common.points")}</span>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Stat label={t("analytics.events")} value={totals.events} testId="participant-stat-events" />
        <Stat label={t("events.checkedIn")} value={totals.checked_in} testId="participant-stat-checked-in" />
        <Stat label={t("profile.noShows")} value={totals.no_shows} testId="participant-stat-no-shows" />
        <Stat label={t("analytics.attendanceRate")} value={`${totals.attendance_rate}%`} testId="participant-stat-rate" />
        <Stat label={t("analytics.approvedProofs")} value={totals.approved} testId="participant-stat-approved" />
        <Stat label={t("home.pending")} value={totals.pending} testId="participant-stat-pending" />
      </div>

      <section className="space-y-3" data-testid="participant-timeline">
        <h2 className="font-display text-lg font-bold">{t("analytics.participationHistory")}</h2>
        {timeline.map((p) => (
          <article key={p.id} className="vy-card p-5" data-testid={`participant-event-${p.event_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <Link to={`/staff/events/${p.event_id}`} className="font-display text-base font-bold hover:text-accent">
                  {p.event_title}
                </Link>
                <p className="text-xs text-muted-foreground">
                  {fmtDate(p.starts_at, locale)}{p.location ? ` · ${p.location}` : ""}
                  {p.partner_name ? ` · ${p.partner_name}` : ""}{p.campaign_name ? ` · ${p.campaign_name}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={p.checked_in_at ? "checked_in" : (p.status || "confirmed")} />
                <span className="font-display text-base font-extrabold tabular-nums">{p.points}</span>
              </div>
            </div>
            {p.submissions.length > 0 && (
              <ul className="mt-3 divide-y divide-border border-t border-border">
                {p.submissions.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`participant-proof-${s.id}`}>
                    <span className="flex-1 text-sm font-semibold">{s.mission_title}</span>
                    <StatusBadge status={s.status} />
                    <span className="text-xs text-muted-foreground">
                      {s.reviewed_at ? `${fmtDate(s.reviewed_at, locale)}${s.reviewer_name ? ` · ${s.reviewer_name}` : ""}` : "—"}
                    </span>
                    <span className="text-xs font-bold tabular-nums">+{s.mission_points}</span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
        {timeline.length === 0 && <p className="text-sm text-muted-foreground">{t("common.none")}</p>}
      </section>

      <section className="vy-card overflow-x-auto p-5" data-testid="participant-ledger">
        <h2 className="font-display text-lg font-bold">{t("nav.ledger")}</h2>
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2">{t("common.reason")}</th>
              <th className="py-2">{t("analytics.event")}</th>
              <th className="py-2">{t("common.points")}</th>
              <th className="py-2">{t("analytics.recordedAt")}</th>
            </tr>
          </thead>
          <tbody>
            {ledger.map((l) => (
              <tr key={l.id} className="border-t border-border" data-testid={`participant-ledger-${l.id}`}>
                <td className="py-3">{l.reason} <span className="text-xs text-muted-foreground">({l.source})</span></td>
                <td className="py-3 text-muted-foreground">{l.event_title || "—"}</td>
                <td className={`py-3 font-bold tabular-nums ${l.points >= 0 ? "" : "text-destructive"}`}>
                  {l.points >= 0 ? "+" : ""}{l.points}
                </td>
                <td className="py-3 text-xs text-muted-foreground">{fmtDate(l.created_at, locale)} · {l.actor_name}</td>
              </tr>
            ))}
            {ledger.length === 0 && <tr><td colSpan={4} className="py-4 text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
